import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ClipboardList } from 'lucide-react';
import { Page } from '@shared/ui/containers/Page';
import { Card } from '@shared/ui/containers/Card';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { Button } from '@shared/ui/primitives/Button';
import { toast } from '@shared/ui/feedback/Toast';
import { fetchPublicSurveyByToken, submitPublicSurveyResponse } from './api';

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

export function PublicSurvey(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ['public-survey', token],
    queryFn: () => fetchPublicSurveyByToken(token),
    enabled: !!token,
    retry: false,
  });

  const startMutation = useMutation({
    mutationFn: () => {
      if (!query.data) throw new Error('Survey is not loaded yet.');
      return submitPublicSurveyResponse(token, {
        survey_template_id: query.data.survey_template_id,
      });
    },
    onSuccess: (response) => {
      const params = new URLSearchParams({
        instance: response.flow_instance_id,
        context: 'survey',
        kind: 'survey',
        title: query.data?.name ?? 'Survey',
        backTo: `/surveys/link/${token}`,
      });
      navigate(`/onboarding?${params.toString()}`);
    },
    onError: (error) => {
      toast.danger("Couldn't start survey", error instanceof Error ? error.message : undefined);
    },
  });

  if (query.isLoading) {
    return (
      <Page>
        <Skeleton className="h-8 w-2/3" />
        <div className="mt-4">
          <Skeleton className="h-32" />
        </div>
      </Page>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Page>
        <ErrorState
          title="Survey link unavailable"
          description="This link may have expired, or the survey is no longer accepting responses."
          onRetry={() => query.refetch()}
        />
      </Page>
    );
  }

  const survey = query.data;
  const isClosed = survey.deadline_at ? new Date(survey.deadline_at).getTime() < Date.now() : false;

  return (
    <Page>
      <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
        <ClipboardList className="h-6 w-6" strokeWidth={1.5} />
      </span>
      <h1 className="display-serif text-display-lg text-ink mt-3">{survey.name}</h1>
      {survey.deadline_at ? (
        <p className="mt-1 text-body-sm text-ink-muted">
          Closes {DATE_FMT.format(new Date(survey.deadline_at))}
        </p>
      ) : null}

      {survey.description ? (
        <Card className="mt-5">
          <p className="whitespace-pre-line text-body text-ink-secondary">{survey.description}</p>
        </Card>
      ) : null}

      <Button
        width="full"
        className="mt-6"
        disabled={isClosed}
        loading={startMutation.isPending}
        onClick={() => startMutation.mutate()}
      >
        {isClosed ? 'Survey closed' : 'Start survey'}
      </Button>
    </Page>
  );
}
